import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { ReplaySubject, Subject } from 'rxjs';

import { delayFor } from 'app/core/utilities/async-utils';
import { BackgroundService } from 'app/modules/social/services/background/background.service';
import { UtilService } from 'app/modules/social/services/util/util.service';
import { Loggable } from 'app/utils/logging/loggable';
import SocialTheme from 'entity/SocialTheme';
import SocialThemeData from 'entity/SocialThemeData';
import RequestStatusType from 'enums/RequestStatusType';
import SocialThemeType from 'enums/SocialThemeType';
import ImageController from 'rest/ImageController';

export enum ImageUploadStatus {
  NOT_STARTED,
  PENDING,
  DONE,
  ERROR,
}

export interface UploadImage {
  SocialThemeType?: SocialThemeType;
  theme?: SocialTheme;
  imageLayer?: 'Far' | 'Mid' | 'Near';
  imageId?: string;
  // eslint-disable-next-line
  imageBytes?: any;
  selectedImage?: string;
}

@Component({
  selector: 'app-modal-upload-image',
  templateUrl: './modal-upload-image.component.html',
  styleUrls: ['./modal-upload-image.component.scss'],
  standalone: false,
})
export class ModalUploadImageComponent extends Loggable implements OnDestroy {
  public closed: Subject<boolean> = new Subject<boolean>();
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected imageSelected = false;
  public imageLogoId?: string;
  public originalImageFile = new Image();
  private imageData?: {
    name: string;
    width?: number;
    height?: number;
    extension: string;
  };
  public selectedImage?: string;
  protected isImageToSmall = false;
  protected imageToCropFile?: File;
  protected imageToCropURI?: string = '';
  public croppedImageEvent?: ImageCroppedEvent;
  private createdTheme?: SocialTheme;
  private layeredImageId?: string;

  private uploadImage?: UploadImage;

  protected uploadStatus: ImageUploadStatus = ImageUploadStatus.NOT_STARTED;

  private imageController = new ImageController();
  protected transform = { scale: 1 };

  @ViewChild('imageCropper') public imageCropper!: ImageCropperComponent;

  public constructor(
    public dialogRef: MatDialogRef<ModalUploadImageComponent>,
    private backgroundService: BackgroundService,
    private utilService: UtilService,
    @Inject(MAT_DIALOG_DATA)
    public data: { roomSize: number; useCropper: boolean; theme: SocialTheme },
  ) {
    super();
  }

  public shouldCreateAccount(value: boolean): void {
    this.closed.next(value);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public confirm(): void {
    if (this.imageToCropFile) {
      this.confirmCropImage();
      return;
    }

    if (
      this.uploadStatus === ImageUploadStatus.NOT_STARTED &&
      this.selectedThemeType === SocialThemeType.SINGLE_IMAGE
    ) {
      void this.handleBackgroundTheme();
      return;
    }

    if (this.isImageDone && this.selectedThemeType === SocialThemeType.LAYERED) {
      this.dialogRef.close({
        SocialThemeType: this.selectedThemeType,
        imageId: this.layeredImageId,
        imageBytes: this.uploadImage?.imageBytes,
        selectedImage: this.uploadImage?.selectedImage,
      });
      return;
    }

    if (this.isImageDone && this.selectedThemeType === SocialThemeType.SINGLE_IMAGE) {
      this.dialogRef.close({ SocialThemeType: this.selectedThemeType, theme: this.createdTheme });
      return;
    }

    this.dialogRef.close(this.uploadImage);
  }

  protected imageCropped(event: ImageCroppedEvent) {
    this.croppedImageEvent = event;
  }

  public uploadFile(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        img.src = reader.result as string;
        img.onload = () => {
          const width = img.width;
          const height = img.height;

          if (this.data.useCropper) {
            if (width < this.data.roomSize || height < this.data.roomSize) {
              this.isImageToSmall = true;
              return;
            }

            if (width > this.data.roomSize && height > this.data.roomSize) {
              this.imageToCropFile = file;
              this.imageToCropURI = reader.result as string;
              return;
            }
          }

          this.receiveImage({ base64: reader.result as string, fileName: file.name, fileType: file.type });
          void this.uploadLayeredImage(file.name, file.type, img.width, img.height, reader.result as string);
        };
      };
    }
  }

  private async uploadLayeredImage(imageName: string, type: string, width: number, height: number, base64: string) {
    if (this.selectedThemeType !== SocialThemeType.LAYERED) {
      return;
    }

    const base64Bytes = base64.split(',')[1];

    if (!base64Bytes) {
      return;
    }

    this.uploadStatus = ImageUploadStatus.PENDING;
    await this.imageController.uploadBase64Image(imageName, type, width, height, base64Bytes).then((res) => {
      this.layeredImageId = res.imageId;
      this.uploadStatus = ImageUploadStatus.DONE;
    });
  }

  private confirmCropImage(): void {
    const image = this.imageCropper.crop();

    if (!image) {
      this.error('Error while cropping the image');
    }

    this.croppedImageEvent = image ?? undefined;

    this.receiveImage({
      base64: image?.base64 ?? '',
      fileName: this.imageToCropFile?.name ?? '',
      fileType: this.imageToCropFile?.type ?? '',
    });
    this.imageToCropFile = undefined;
    this.imageToCropURI = undefined;
  }

  private receiveImage({ base64, fileName, fileType }: { base64: string; fileName: string; fileType: string }) {
    this.selectedImage = base64;
    this.imageData = {
      name: fileName,
      extension: fileType,
    };
    this.imageSelected = true;

    this.uploadImage = {
      SocialThemeType: this.selectedThemeType,
      selectedImage: base64,
      imageBytes: base64,
    };
  }

  private async handleBackgroundTheme() {
    this.uploadStatus = ImageUploadStatus.PENDING;

    const image = this.croppedImageEvent?.base64 ? this.croppedImageEvent?.base64 : this.uploadImage?.imageBytes;

    if (!image) {
      this.error('Error while getting image');
      return;
    }

    const block = image.split(',')[1];

    if (!block) {
      return;
    }

    const themeData: SocialThemeData = {
      theme: this.data.theme,
      imageBytes: block,
    };

    if (this.data?.theme?.socialThemeId) {
      await this.backgroundService
        .updateSocialTheme(themeData)
        .then((res) => {
          this.checkThemeStatus(res);
        })
        .catch((err) => {
          this.error(err);
          this.uploadStatus = ImageUploadStatus.ERROR;
        });
      return;
    }

    await this.backgroundService
      .createCustomTheme(themeData)
      .then((res) => {
        this.checkThemeStatus(res);
      })
      .catch((err) => {
        this.error(err);
        this.uploadStatus = ImageUploadStatus.ERROR;
      });
  }

  private checkThemeStatus(theme: SocialTheme) {
    this.utilService
      .getRequestStatus(theme.uploadStatus?.requestStatusId ?? '')
      .then(async (requestStatus) => {
        if (requestStatus.status === RequestStatusType.COMPLETED) {
          await this.getCreatedThemeData(theme?.socialThemeId);
          this.uploadStatus = ImageUploadStatus.DONE;
          return;
        }
        await delayFor(1000);
        this.checkThemeStatus(theme);
      })
      .catch((error) => {
        this.error(error);
      });
  }

  private async getCreatedThemeData(socialThemeId?: string) {
    if (!socialThemeId) {
      this.error('Error while getting data from created theme, socialThemeId is undefined', socialThemeId);
      return;
    }

    await this.backgroundService
      .getSocialTheme(socialThemeId)
      .then((theme) => (this.createdTheme = theme))
      .catch((err) => this.error('Error durring get SocialTheme', err));
  }

  protected selectNewImage() {
    this.isImageToSmall = false;
    this.imageSelected = false;
    this.imageToCropFile = undefined;
  }

  protected changeZoom(size: number) {
    const zoom = size / 100 + 1;
    this.transform = { scale: zoom };
  }

  private get selectedThemeType() {
    return this.data?.theme?.type;
  }

  protected get isStartedUpload() {
    return this.uploadStatus !== ImageUploadStatus.NOT_STARTED;
  }

  protected get isImageUploading() {
    return this.uploadStatus === ImageUploadStatus.PENDING;
  }

  protected get isImageDone() {
    return this.uploadStatus === ImageUploadStatus.DONE;
  }
}
